import { trackOutputAction } from 'analytics';
import Icon, { IconType } from 'components/Icon';
import { SegmentedButton, SegmentedControl } from 'components/Widget/Button/SegmentedControl';
import DropdownMenu from 'components/Widget/DropdownMenu';
import { Tooltip } from 'components/Widget/Tooltip';
import { Frame, FrameStatus } from 'dragonmouse-shared/interfaces';
import { MenuItemAction } from 'menu/MenuItem';
import JSZip from 'jszip';
import { useMemo, useCallback } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { toast } from 'react-toastify';
import { selectActiveScene } from 'redux/db/reducers';
import {
	canEdit,
	canExportSvg,
	getFrameExportHtml,
	getFrameImage,
	getFrameSvg,
} from 'redux/db/util';
import { alignSelectedFrames, displayViewOnlyModal, FrameSide } from 'redux/mode/util';
import { RootReducer } from 'redux/reducers';
import { useTheme } from 'styled-components';
import { downloadBlob, ExportFormat } from 'utils';
import Section from '../Section';
import * as Base from '../styles';
import * as S from './styles';

interface MultipleFramePropertiesProps {
	frames: Frame[];
}
const MultipleFrameProperties = ({ frames }: MultipleFramePropertiesProps) => {
	const theme = useTheme();
	const tooltipId = 'header-tooltip';

	const { scene } = useSelector(
		(r: RootReducer) => ({
			scene: selectActiveScene(r.db, r.mode.activeSceneId),
		}),
		shallowEqual
	);

	//------------------------------------------------------------------------------------------------
	// HANDLERS
	//------------------------------------------------------------------------------------------------

	const onExportMultiple = useCallback(
		async (format: ExportFormat) => {
			const plural = frames.length === 1 ? '' : 's';
			const description = `${frames.length} ${format.toUpperCase()}${plural}`;
			const promiseToExport = new Promise<void>((resolve, reject) => {
				(async () => {
					try {
						const zip = new JSZip();
						const zipFilename = `${format}s.zip`;
						const usedFilenames = new Map<string, number>();

						const exportConfig = {
							png: {
								action: 'export_png' as const,
								getBlob: getFrameImage,
							},
							svg: {
								action: 'export_svg' as const,
								getBlob: getFrameSvg,
							},
							html: {
								action: 'export_html' as const,
								getBlob: getFrameExportHtml,
							},
						};

						const config = exportConfig[format];

						for (const frame of frames) {
							if (format === 'svg' && !canExportSvg([frame])) {
								continue;
							}

							const baseName = frame.title || 'untitled';
							let count = usedFilenames.get(baseName) || 0;
							let filename = baseName;

							if (count > 0) {
								filename = `${baseName} (${count})`;
							}

							usedFilenames.set(baseName, count + 1);
							trackOutputAction(config.action);

							const blob = await config.getBlob(frame);
							zip.file(`${filename}.${format}`, blob);
						}

						const zipBlob = await zip.generateAsync({ type: 'blob' });
						downloadBlob(zipBlob, zipFilename);
					} catch (error) {
						console.error('Failed to create zip file:', error);
						throw error;
					}
				})()
					.then(() => resolve())
					.catch((error) => reject(error));
			});

			toast.promise(promiseToExport, {
				pending: `Exporting ${description} ...`,
				success: `Exported ${description}`,
				error: `Failed to export ${description}`,
			});
		},
		[frames]
	);

	//------------------------------------------------------------------------------------------------
	// MENU ITEMS
	//------------------------------------------------------------------------------------------------
	const downloadMenuItems = useMemo(() => {
		const items = [
			new MenuItemAction('Download PNG', '', () => onExportMultiple('png')),
			...(canExportSvg(frames)
				? [new MenuItemAction('Download SVG', '', () => onExportMultiple('svg'))]
				: []),
			new MenuItemAction('Download HTML', '', () => onExportMultiple('html')),
		];
		return items;
	}, [frames, onExportMultiple]);

	//------------------------------------------------------------------------------------------------
	// RENDERING
	//------------------------------------------------------------------------------------------------

	const iconColor = theme.gray[700];
	const isDisabled = frames.some((frame) => frame.metadata.status !== FrameStatus.READY);

	const align = (direction: FrameSide): void => {
		if (!canEdit(scene)) {
			displayViewOnlyModal();
			return;
		}
		alignSelectedFrames(direction);
	};

	const headerLabel = `${frames.length} Frames`;

	return (
		<Base.Base>
			<Base.Divider />
			<S.HeaderBase>
				<S.HeaderLabel>{headerLabel}</S.HeaderLabel>
				<S.HeaderActionsSection>
					<DropdownMenu menuItems={downloadMenuItems}>
						<S.DropdownTrigger
							data-tooltip-id={tooltipId}
							data-tooltip-content="Download"
							style={{ opacity: isDisabled ? 0.5 : 1, pointerEvents: isDisabled ? 'none' : 'auto' }}
						>
							<Icon
								type={IconType.LINE_ARROW_DOWN}
								width={16}
								height={16}
								color={theme.gray[1000]}
							/>
						</S.DropdownTrigger>
					</DropdownMenu>
					<Tooltip id={tooltipId} placement="top" />
				</S.HeaderActionsSection>
			</S.HeaderBase>
			<Base.ScrollingContainer>
				<Section label="Alignment">
					<Base.Row>
						<SegmentedControl>
							<SegmentedButton
								key="align-left"
								onSelected={() => align('left')}
								tooltip="Align Left"
							>
								<Icon type={IconType.ALIGN_ITEM_LEFT} width={18} height={18} color={iconColor} />
							</SegmentedButton>
							<SegmentedButton
								key="align-right"
								onSelected={() => align('right')}
								tooltip="Align Right"
							>
								<Icon type={IconType.ALIGN_ITEM_RIGHT} width={18} height={18} color={iconColor} />
							</SegmentedButton>
						</SegmentedControl>
						<SegmentedControl>
							<SegmentedButton key="align-top" onSelected={() => align('top')} tooltip="Align Top">
								<Icon type={IconType.ALIGN_ITEM_TOP} width={18} height={18} color={iconColor} />
							</SegmentedButton>
							<SegmentedButton
								key="align-bottom"
								onSelected={() => align('bottom')}
								tooltip="Align Bottom"
							>
								<Icon type={IconType.ALIGN_ITEM_BOTTOM} width={18} height={18} color={iconColor} />
							</SegmentedButton>
						</SegmentedControl>
					</Base.Row>
					<Base.Spacer />
				</Section>
				<Base.Spacer />
			</Base.ScrollingContainer>
		</Base.Base>
	);
};

export default MultipleFrameProperties;
